import { useState, useEffect, type ReactElement } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import * as amplitude from "@amplitude/analytics-browser";

import type { IButton } from "libs/design-system/src/types/Button/button";
import type { IBadge } from "libs/design-system/src/types/Badge/badge";
import {
  ArrowsRepeat,
  Check,
  CircleExclamation,
  EllipsisVertical,
  MoneyCheckDollar,
  Xmark,
} from "@unified-checkout/design-system/icons/index";
import {
  Badge,
  Breadcrumbs,
  Button,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  formatAmountWithCommas,
  FormatCurrencyForDisplay,
  Modal,
  SkeletonBase
} from "@unified-checkout/design-system";

import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import {
  TRANSACTIONS_EVENTS,
  TRANSACTIONS_EVENTS_PARAMS,
  TRANSACTIONS_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/transactions";
import {
  MAX_MODAL_WIDTH,
  MAX_NOTIFICATION_WIDTH,
  MODAL_TYPES,
  MODAL_STEP_STATUS,
  NOTIFICATION_TIMEOUT,
  VISIBLE_ACTION_BTNS,
} from '@app/dashboard/constants/transactions';
import { FOUR_SECONDS, OTP_LENGTH } from "@app/dashboard/constants/shared";
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES
} from "@app/dashboard/constants/tracking_events/system";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { useLangStore } from "@app/dashboard/store/lang";
import type {
  ISingleTransactionHeader,
  ITransactionRefund,
  TRefundStep,
  TTransactionStatus,
  TModalAction
} from "@app/dashboard/types/innerLayout/transactions";
import { useApplyThenNavigate } from "@app/dashboard/hooks/useApplyThenNavigate";
import { notify } from '@app/dashboard/utils/notifier';
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { halanRefundSubmitOtp, refundTransaction } from "@app/dashboard/services/inner_layout/transactions/refund";
import { voidTransaction } from "@app/dashboard/services/inner_layout/transactions/void";
import { captureTransaction } from "@app/dashboard/services/inner_layout/transactions/capture";
import { onToasterShowHandler } from "@app/dashboard/utils/tracking/onToasterShowHandler";
import { getMerchantToken } from "@app/dashboard/utils/localStorageUtils";
import { formatToLocalizedDateTime } from "@app/dashboard/utils/dateFormats";
import { convertAmountToCents, convertCentsToMajorUnit } from "@app/dashboard/utils/convertAmounts";
import { numberMasking } from "@app/dashboard/utils/numberMasking";
import {
  isSouhoola,
  HALAN,
  INSTAPAY,
  OMANNET,
  maxRefundAmount,
  isKnet,
  calculateInstantRefundFee,
  isExternallyInitiatedRefund,
} from "../utils";

import { RenderCaptureModalContent, RenderOTPModalBody, RenderRefundModalContent } from "../modals/ModalBodyContent";

import styles from "./header.module.scss";

type TActions = 'void' | 'refund' | 'capture';

export const Header = ({
  data: selectedTransaction,
  isError,
  isLoading,
  refetchData
}:
  ISingleTransactionHeader
) => {
  const id = selectedTransaction?.id;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isPayMeRoute = location.pathname.startsWith('/payme/');
  const { lang } = useLangStore();
  const screenWidth = useScreenWidth();
  const applyThenNavigate = useApplyThenNavigate();


  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isModalOpen, setIsModalOpen] = useState({
    refund: false,
    void: false,
    capture: false,
    otp: false
  });

  const [isPartialRefund, setIsPartialRefund] = useState(false);
  const [partialAmount, setPartialAmount] = useState({ value: "", error: "" });
  const [refundPin, setRefundPin] = useState<{ value: string | undefined, error: string }>({ value: undefined, error: "" });
  const [captureAmountField, setCaptureAmountField] = useState({ value: "", error: "" });
  const [productsRefund, setProductsRefund] = useState<{ price: number, ids: string }>();
  const [modalStep, setModalStep] = useState<{ type: Exclude<TRefundStep, "otp">, message: string }>({ type: MODAL_STEP_STATUS.START, message: '' });
  const [otpRefundData, setOtpRefundData] = useState<{
    transactionId?: number,
    phoneNumber?: string,
    otp?: string,
    error?: string
  }>();
  const [params, setParams] = useState<ITransactionRefund>();
  
  useEffect(() => {
    id && setParams((prevParams) => ({
      ...prevParams,
      transactionId: Number(id)
    }));
  }, [id]);

  useEffect(() => {
    isModalOpen.refund === false && setPartialAmount({ value: "", error: "" });
  }, [isModalOpen.refund]);

  const handleCloseModal = (action: TModalAction) => {
    setIsModalOpen({ ...isModalOpen, [action]: false });
  }

  const targetRouteAfterSuccess = (type: Exclude<TRefundStep, "otp">) => {
    if (type === MODAL_STEP_STATUS.SUCCESS) {
      return isPayMeRoute ? INNER_ROUTES.PAYME : INNER_ROUTES.TRANSACTIONS
    }
  }

  const handleNotificationState = (
    action: TModalAction,
    type: "success" | "error" | null,
    message: string,
    trackingCode?: string
  ) => {

    handleCloseModal(action);
    
    notify[type as 'success' | 'error'](message, {
      timeout: NOTIFICATION_TIMEOUT,
      maxWidth: MAX_NOTIFICATION_WIDTH,
      icon: type === 'success' ? Check : Xmark,
      onShowHandler: () => onToasterShowHandler({
        type: type,
        trackingCode: trackingCode,
        msg: message,
        srcFeature: SYSTEM_EVENTS_SRC_FEATURES.TRANSACTIONS.DETAILS
      })
    })

    setTimeout(() => {
      const route = targetRouteAfterSuccess(type as Exclude<TRefundStep, "otp">);

      if (route) {
        navigate(route);
      };

    }, FOUR_SECONDS);

  };

  const handleModalStatus = ({ message, type, action }: { message: string, type: Exclude<TRefundStep, "otp">, action: TModalAction }) => {
    setModalStep({ type, message });

    setTimeout(() => {
      const route = targetRouteAfterSuccess(type as Exclude<TRefundStep, "otp">) as string;

      const resetBeforeNavigate = () => {
        handleCloseModal(action);
        setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
      };

      if (route) {
        applyThenNavigate(resetBeforeNavigate, route);
      } else {
        resetBeforeNavigate();
      }
    }, FOUR_SECONDS);
  }

  const getBadgeProps = (status: TTransactionStatus) => {

    const isSuccess = status === "success";
    const isPending = status === "pending";
    const isRefunded = status === "refunded";

    return {
      text: status_text,
      color: isSuccess ? "green" : isPending ? "yellow" : isRefunded ? "gray" : "red",
    } as IBadge;
  };

  const handleModalClose = (type: TModalAction) => {

    if (type === MODAL_TYPES.REFUND) {
      setPartialAmount({ ...partialAmount, value: "" });
      setIsPartialRefund(false);
      setIsModalOpen({ ...isModalOpen, refund: false });
      setRefundPin({ value: undefined, error: "" });
    }
  };

  const {
    amount,
    refunded_amount_cents,
    amount_cents,
    currency,
    status,
    status_text,
    created_at,
    success,
    pending,
    source_data,
    is_refundable,
    is_voidable,
    is_capturable,
    is_product_refund,
    is_refund_pin_required,
    to_be_refunded_products,
    is_otp_required_refund,
    caf_details
  } = selectedTransaction || {};

  const refundType = is_product_refund ? "products" : "transaction";
  const canPartialRefund = refundType === "transaction";
  const toBeRefundedProducts = to_be_refunded_products?.map((product: any) => ({
    id: product.id,
    name: product.name,
    category: product.category,
    price: product.price,
    currency: product.currency,
    quantity: product.quantity
  }));

  const refundBtnDisabled = () => {
    if (refundType === "transaction") {
      return (
        (canPartialRefund && isPartialRefund && (!partialAmount.value || !!partialAmount.error)) ||
        (is_refund_pin_required && !refundPin.value) ||
        isRefundLoading
      )
    } else if (refundType === "products") {
      return !productsRefund?.ids || !productsRefund?.price || isRefundLoading
    }
  };
  
  const {
    isPending: isRefundLoading,
    mutate: mutateRefundTransaction
  } = useMutation({
    mutationKey: [QUERY_KEYS.REFUND_TRANSACTION],
    mutationFn: () => params ? refundTransaction(params) : Promise.resolve(null),
    onSuccess: (response) => {
      const responseData = response?.data?.data;
      const { success, pending, id, source_data, data } = responseData;
      const { mobile_number } = source_data;
      const message = ((m) => (typeof m === 'string' ? m : m?.message))(responseData?.data?.message ?? responseData?.data?.data?.message);

      if (success === true && pending === false) {
        handleModalStatus({
          message: t(
            'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.status.successMessage'
          ),
          type: MODAL_STEP_STATUS.SUCCESS,
          action: MODAL_TYPES.REFUND,
        });
      } else if (data.success && data.pending && source_data?.instant_refund) {
        handleModalStatus({
          message: t(
            'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.status.refundPendingMessage'
          ),
          type: MODAL_STEP_STATUS.SUCCESS,
          action: MODAL_TYPES.REFUND,
        });
      } else if (
        pending === true &&
        is_otp_required_refund
      ) {
        setOtpRefundData({ transactionId: id, phoneNumber: mobile_number });
        setIsModalOpen({ ...isModalOpen, otp: true });
      } else if (success === false && isSouhoola(source_data?.type)) {
        handleModalStatus({
          message: message || t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.errors.suohoolaPartialRefundMsg'),
          type: MODAL_STEP_STATUS.FAILED,
          action: MODAL_TYPES.REFUND,
        });
      } else if (isExternallyInitiatedRefund(success, pending, source_data?.type)) {
        handleModalStatus({
          message: t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.status.externalRefundSuccess'),
          type: MODAL_STEP_STATUS.SUCCESS,
          action: MODAL_TYPES.REFUND,
        });
      } else {
        handleModalStatus({
          message: message || t('errors.somethingWentWrong'),
          type: MODAL_STEP_STATUS.FAILED,
          action: MODAL_TYPES.REFUND,
        });
      }
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const status = response?.status;
      const message = response?.data?.message;
      let errorMessage = t('innerLayout.errors.smthWentWrong');
      let action = MODAL_TYPES.REFUND as TModalAction;

      if (response) {
        if (is_otp_required_refund || isKnet(source_data?.type)) {
          errorMessage = message || t('innerLayout.errors.smthWentWrong');
          action = MODAL_TYPES.OTP;
        } else if (status === 422) {
          errorMessage = message || t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.status.failedMessage');
        } else if (status === 504 && source_data?.type === OMANNET) {
          errorMessage = t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.errors.omannetFailureMessage');
        } else if (status === 400 && message) {
          errorMessage = message;
        }
      }

      handleModalStatus({
        message: errorMessage,
        type: MODAL_STEP_STATUS.FAILED,
        action,
      });
    }
  });

  const {
    isPending: isVoidLoading,
    mutate: mutateVoidTransaction
  } = useMutation({
    mutationKey: [QUERY_KEYS.VOID_TRANSACTION],
    mutationFn: () => id ? voidTransaction({ transactionId: Number(id) }) : Promise.resolve(null),
    onSuccess: (response) => {
      const { success, pending } = response?.data?.data || {};

      if (success === true && pending === false) {
        const trackingCode = SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.VOID_INITIATION;

        handleNotificationState(
          'void',
          'success',
          t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.status.voidInitiatedSuccessfully'),
          trackingCode
        );
      } else {
        let msg, trackingCode;

        if (source_data?.type === INSTAPAY) {
          msg = t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.errors.trnxCantBeCanceled');
          trackingCode = SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.CANCEL_TRANSACTION
        } else {
          msg = t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.errors.trnxCantBeVoided')
          trackingCode = SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.VOID_TRANSACTION
        }

        handleNotificationState(
          'void',
          'error',
          msg,
          trackingCode
        );
      }
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const serverMessage = (axiosErr?.response?.data as any)?.message;
      let msg, trackingCode;

      if (source_data?.type === INSTAPAY) {
        msg = t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.errors.trnxCantBeCanceled');
        trackingCode = SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.CANCEL_TRANSACTION
      } else {
        msg = t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.errors.trnxCantBeVoided')
        trackingCode = SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.VOID_TRANSACTION
      }

      const errorMessage = axiosErr?.response?.status === 422 ?
        msg
        :
        serverMessage || t('innerLayout.errors.smthWentWrong');

      handleNotificationState(
        'void',
        'error',
        errorMessage,
        trackingCode
      );
    }
  });

  const {
    isPending: isCaptureLoading,
    mutate: mutateCaptureTransaction
  } = useMutation({
    mutationKey: [QUERY_KEYS.CAPTURE_TRANSACTION],
    mutationFn: () => params ? captureTransaction(params as any) : Promise.resolve(null),
    onSuccess: (response) => {
      const { success } = response?.data?.data;

      if (success) {
        handleNotificationState(
          'capture',
          'success',
          t('innerLayout.transactionsPage.singleTransactionsPage.modals.capture.status.successfulCapture'),
          SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.CAPTURE_TRANSACTION
        );
      } else {
        handleNotificationState(
          'capture',
          'error',
          t('innerLayout.transactionsPage.singleTransactionsPage.modals.capture.errors.cantBeCaptured'),
          SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.CAPTURE_TRANSACTION
        );
      }
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const status = axiosErr?.response?.status;

      handleNotificationState(
        'capture',
        'error',
        status === 400 ?
          t('innerLayout.transactionsPage.singleTransactionsPage.modals.capture.errors.failureMessage400')
          :
          status === 404 ?
            t('innerLayout.transactionsPage.singleTransactionsPage.modals.capture.errors.failureMessage404')
            :
            t('innerLayout.errors.smthWentWrong'),
        SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.CAPTURE_TRANSACTION
      )
    },
  });

  const {
    isPending: isSubmitOtpLoading,
    mutate: mutateSubmitOtpTransaction
  } = useMutation({
    mutationKey: [QUERY_KEYS.HALAN_SUBMIT_OTP],
    mutationFn: () => halanRefundSubmitOtp({
      otp: otpRefundData?.otp as string,
      transactionId: otpRefundData?.transactionId as number,
      ...(
        source_data?.type === HALAN ?
          { authToken: getMerchantToken() }
          :
          {}
      )
    }),
    onSuccess: (response) => {
      const { success, message } = response?.data?.data;

      if (success === true) {
        handleNotificationState(
          'otp',
          'success',
          message,
          SYSTEM_EVENTS_GROUPING_CODES.TRANSACTIONS.DETAILS.OTP_SUBMIT
        );
      } else {
        setOtpRefundData({ ...otpRefundData, error: message || t('errors.somethingWentWrong') });
      }
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const { message } = response?.data || {};

      setOtpRefundData({ ...otpRefundData, error: message || t('errors.somethingWentWrong') });
    }
  });

  const handleRefund = async () => {

    await setParams((prevParams: any) => {
      const isPartialAmountEqualsCurrentAmount = amount === convertAmountToCents(Number(partialAmount.value), currency);
      const amountCents = refundType === 'products' ?
        Math.round(convertAmountToCents(productsRefund?.price as number, currency, true))
        :
        Math.round(convertAmountToCents(Number(partialAmount.value), currency));

      const partialRefund = (
        isPartialRefund &&
        canPartialRefund &&
        !isPartialAmountEqualsCurrentAmount
      );

      const newParams = {
        ...prevParams,
        ...((partialRefund || refundType === 'products') ? { amount: amountCents } : {}),
        ...(refundType === 'products' ? { productsIds: productsRefund?.ids } : {}),
        ...(source_data?.type === 'shahry' ? { refundReason: "USER REFUNDED" } : {}),
        ...(is_refund_pin_required ? { pin: refundPin.value } : {}),
        ...(!partialRefund && source_data.instant_refund ? { amount: maxRefundAmount(selectedTransaction) } : {}),
      }

      if (!partialRefund && newParams?.amount) {
        delete newParams.amount;
      }

      return newParams;
    });

    mutateRefundTransaction();
  };

  const handleCapture = async () => {

    await setParams((prevParams) => {
      const amountCents = convertAmountToCents(Number(captureAmountField.value), currency);
      const roundedAmountCents = Math.round(amountCents);

      return {
        ...prevParams,
        amount: roundedAmountCents,
      } as any;
    });

    mutateCaptureTransaction();
  };

  const handleActionBtnClick = (type: TActions) => {
    setIsModalOpen({ ...isModalOpen, [type]: true });

    amplitude.track(TRANSACTIONS_EVENTS.TRNX_ACTION_CLICKED, {
      [TRANSACTIONS_EVENTS_PARAMS.TRNX_ID]: id,
      [TRANSACTIONS_EVENTS_PARAMS.ACTION]: type,
      [TRANSACTIONS_EVENTS_PARAMS.STATUS_BEFORE]: status,
    });
  };

  const handleActionBtnConfirm = (type: TActions) => {

    if (type === "refund") {
      handleRefund();
    } else if (type === "void") {
      mutateVoidTransaction();
    } else if (type === "capture") {
      handleCapture();
    };

    amplitude.track(TRANSACTIONS_EVENTS.TRNX_ACTION_CONFIRMED, {
      [TRANSACTIONS_EVENTS_PARAMS.TRNX_ID]: id,
      [TRANSACTIONS_EVENTS_PARAMS.ACTION]: type,
      [TRANSACTIONS_EVENTS_PARAMS.CONFIRMATION_METHOD]: TRANSACTIONS_EVENTS_VALUES.CONFIRMATION_METHOD.MODAL,
    });
  };


  const commonModalBtnProps: Record<'primary' | 'secondary', Partial<IButton>> = {
    primary: { color: "primary", variant: "contained" },
    secondary: { color: "secondary", variant: "outlined" }
  };

  const refundModalBtnProps: IButton[] = [
    {
      ...commonModalBtnProps.primary,
      text: t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.actions.refundTransaction'),
      disabled: refundBtnDisabled(),
      onClickHandler: () => handleActionBtnConfirm('refund'),
      loading: isRefundLoading,
    },
    {
      ...commonModalBtnProps.secondary,
      text: t('form.cancel'),
      onClickHandler: () => setIsModalOpen({ ...isModalOpen, refund: false })
    }
  ];

  const voidModalBtnProps: IButton[] = [
    {
      ...commonModalBtnProps.primary,
      text: t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.actions.yesVoid'),
      disabled: isVoidLoading /* || hasExceededCancelTime(selectedTransaction) */,
      onClickHandler: () => handleActionBtnConfirm('void'),
      loading: isVoidLoading,
    },
    {
      ...commonModalBtnProps.secondary,
      text: t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.actions.noGoBack'),
      onClickHandler: () => setIsModalOpen({ ...isModalOpen, void: false })
    }
  ];

  const captureModalBtnProps: IButton[] = [
    {
      ...commonModalBtnProps.primary,
      text: t('innerLayout.transactionsPage.singleTransactionsPage.capture'),
      disabled: isCaptureLoading || !!captureAmountField.error || !captureAmountField.value,
      onClickHandler: () => handleActionBtnConfirm('capture'),
      loading: isCaptureLoading,
    },
    {
      ...commonModalBtnProps.secondary,
      text: t('form.cancel'),
      onClickHandler: () => setIsModalOpen({ ...isModalOpen, capture: false })
    }
  ];

  const otpModalBtnProps: IButton[] = [
    {
      ...commonModalBtnProps.primary,
      text: t('form.submit'),
      disabled: ((otpRefundData?.otp?.length !== OTP_LENGTH) || isSubmitOtpLoading),
      loading: isSubmitOtpLoading || isRefundLoading,
      onClickHandler: () => {
        setOtpRefundData({ ...otpRefundData, error: "" });
        mutateSubmitOtpTransaction();
      },
    },
    {
      ...commonModalBtnProps.secondary,
      text: t('form.cancel'),
      onClickHandler: () => setIsModalOpen({ ...isModalOpen, otp: false })
    }
  ];


  const headerActionBtnsSharedProps = {
    size: "md",
    color: "secondary",
    variant: "outlined",
  };

  const headerActionBtns: IButton[] = [
    ... (
      is_refundable ?
        [{
          ...headerActionBtnsSharedProps,
          leadingIcon: ArrowsRepeat,
          text: t('innerLayout.transactionsPage.singleTransactionsPage.refund'),
          onClickHandler: () => handleActionBtnClick('refund')
        }] : []
    ),
    ... (
      is_voidable ?
        [{
          ...headerActionBtnsSharedProps,
          leadingIcon: Xmark,
          text: t('innerLayout.transactionsPage.singleTransactionsPage.void'),
          onClickHandler: () => handleActionBtnClick('void')
        }] : []
    ),
    ... (
      is_capturable ?
        [{
          ...headerActionBtnsSharedProps,
          leadingIcon: MoneyCheckDollar,
          text: t('innerLayout.transactionsPage.singleTransactionsPage.capture'),
          onClickHandler: () => handleActionBtnClick('capture')
        }] : []
    )
  ] as IButton[];

  const visibleActionBtns = headerActionBtns.slice(0, VISIBLE_ACTION_BTNS);
  const collapsedActionBtns = headerActionBtns.slice(VISIBLE_ACTION_BTNS);

  const MODAL_HANDLERS: Record<string, () => ReactElement | null> = {
    [MODAL_TYPES.REFUND]: () => (
      <RenderRefundModalContent
        modalStep={modalStep}
        lang={lang}
        amount={amount}
        caf_detail={caf_details}
        currency={currency}
        refundType={refundType}
        toBeRefundedProducts={toBeRefundedProducts}
        canPartialRefund={canPartialRefund}
        isPartialRefund={isPartialRefund}
        partialAmount={partialAmount}
        is_refund_pin_required={is_refund_pin_required || false}
        refundPin={refundPin}
        selectedTransaction={selectedTransaction}
        source_data={source_data}
        setIsPartialRefund={setIsPartialRefund}
        setProductsRefund={setProductsRefund}
        setPartialAmount={setPartialAmount}
        setRefundPin={setRefundPin}
        paymentMethod={source_data?.type}
      />
    ),

    [MODAL_TYPES.CAPTURE]: () => (
      <RenderCaptureModalContent
        lang={lang}
        amount={amount}
        createdAt={created_at}
        captureAmountField={captureAmountField}
        currency={currency}
        pending={pending}
        selectedTransaction={selectedTransaction}
        setCaptureAmountField={setCaptureAmountField}
        success={success}
        paymentMethod={source_data?.type}
      />
    ),

    [MODAL_TYPES.OTP]: () =>
      RenderOTPModalBody({
        otpRefundData: otpRefundData ?? {},
        setOtpRefundData,
      }),
  };

  const renderModalContent = (modalType: keyof typeof MODAL_HANDLERS) => {
    const handler = MODAL_HANDLERS[modalType];
    return handler ? handler() : null;
  };


  return (
    !isError &&
    <div className={styles["header"]}>

      <div className={styles["header__breadcrumbs"]}>
        {
          isLoading ?
            <SkeletonBase height="1.5rem" />
            :
            <Breadcrumbs
              linksProps={[
                {
                  text: isPayMeRoute
                    ? t('innerLayout.navbar.payme')
                    : t('innerLayout.navbar.transactions'),
                  onClickHandler: () => navigate(
                    isPayMeRoute ? INNER_ROUTES.PAYME : INNER_ROUTES.TRANSACTIONS
                  ),
                  active: true
                },
                {
                  text: t('innerLayout.transactionsPage.singleTransactionsPage.trnxId').replace('{{id}}', id),
                }
              ]}
            />
        }
      </div>

      {
        isLoading ?
          <SkeletonBase height="3.75rem" />
          :
          <div className={styles["header__main-info-box"]}>

            <div className={styles["header__main-info-box__info"]}>

              <div className={styles["header__main-info-box__info__amount-status-box"]}>
                <span>
                  {
                    amount && (
                      <>
                        {lang === 'en' && FormatCurrencyForDisplay(currency)}
                        {' '}{formatAmountWithCommas({ amount, currency })}{' '}
                        {lang === 'ar' && FormatCurrencyForDisplay(currency)}
                      </>
                    )
                  }
                </span>

                <Badge
                  size="md"
                  {...getBadgeProps(status)}
                />
              </div>

              <div className={styles["header__main-info-box__info__date"]}>
                {`${t('innerLayout.transactionsPage.singleTransactionsPage.created')}
                ${created_at && formatToLocalizedDateTime(created_at, lang)}`}
              </div>

            </div>

            <div className={styles["header__main-info-box__actions"]}>
              {
                visibleActionBtns?.map((btn, index) => {
                  return <Button
                    key={`single-transaction-header-action-btn-${index}`}
                    {...btn}
                  />
                })
              }

              {
                !!collapsedActionBtns.length &&
                <Dropdown width="fit-content">
                  <DropdownButton
                    leadingIcon={EllipsisVertical}
                    hideDropdownChevron
                  />
                  <DropdownMenu
                    width="fit-content"
                    horizontalPlacement="end"
                  >
                    {
                      collapsedActionBtns.map((actionBtn, index) =>
                        <DropdownMenuItem
                          text={actionBtn.text}
                          icon={actionBtn.leadingIcon as ReactElement<'svg'>}
                          onClickHandler={actionBtn.onClickHandler}
                          key={`single-trnx-header-action-btn-${index}`}
                        />
                      )
                    }
                  </DropdownMenu>
                </Dropdown>
              }
            </div>

          </div>
      }
      {
        <Modal
          isMobile={isMobile}
          lang={lang}
          open={isModalOpen.refund}
          maxWidth={MAX_MODAL_WIDTH}
          width={MAX_MODAL_WIDTH}
          closeOnClickOutside={false}
          onCloseHandler={() => handleModalClose('refund')}
          {
          ...(modalStep.type === MODAL_STEP_STATUS.START && {
            headerIcon: ArrowsRepeat,
            btnsProps: refundModalBtnProps,
          })
          }
          hasCloseIcon={modalStep.type !== MODAL_STEP_STATUS.START}
          title={
            modalStep.type === MODAL_STEP_STATUS.START &&
            <>
              <span>{t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.refundPayment').replace('{{id}}', id)}</span>
              {
                !!selectedTransaction?.source_data?.instant_refund &&
                <span className={styles["header__instant-refund-tag"]}>{
                  t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.instantRefund')
                }</span>
              }
            </> ||
            ''
          }
        >
          {renderModalContent(MODAL_TYPES.REFUND)}
        </Modal>
      }
      {
        <Modal
          isMobile={isMobile}
          lang={lang}
          open={isModalOpen.void}
          maxWidth={MAX_MODAL_WIDTH}
          closeOnClickOutside={false}
          state="danger"
          headerIcon={CircleExclamation}
          title={
            // hasExceededCancelTime(selectedTransaction) ?
            //   t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.errors.maxCancelTimeElapsed')
            //   :
            t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.voidTransaction').replace('{{id}}', id)
          }
          supportingText={t('innerLayout.transactionsPage.singleTransactionsPage.modals.void.voidTransactionHint')}
          btnsProps={voidModalBtnProps}
        />
      }

      {
        <Modal
          isMobile={isMobile}
          lang={lang}
          open={isModalOpen.capture}
          maxWidth={MAX_MODAL_WIDTH}
          closeOnClickOutside={false}
          onCloseHandler={() => setCaptureAmountField({ value: "", error: "" })}
          headerIcon={MoneyCheckDollar}
          title={t('innerLayout.transactionsPage.singleTransactionsPage.modals.capture.transactionDetails').replace('{{id}}', id)}
          btnsProps={captureModalBtnProps}
          isOverlayAboveModal
        >
          {renderModalContent(MODAL_TYPES.CAPTURE)}
        </Modal>
      }

      {
        <Modal
          isMobile={isMobile}
          lang={lang}
          open={isModalOpen.otp}
          maxWidth={MAX_MODAL_WIDTH}
          width={"fit-content"}
          closeOnClickOutside={false}
          title={t('form.xDigitsCode').replace('{{num}}', OTP_LENGTH)}
          supportingText={t('form.enterTheCode').replace('{{phoneNum}}', numberMasking(otpRefundData?.phoneNumber as string))}
          // onChangeHandler={(otp: any) => setOtpRefundData({ ...otpRefundData, otp, error: "" })}
          errorMsg={otpRefundData?.error}
          btnsProps={otpModalBtnProps}
        >
          {renderModalContent(MODAL_TYPES.OTP)}
        </Modal>
      }
    </div>
  )
};